// ✅ src/pages/VehicleV2/DashBoardReport.jsx

import React, { useEffect, useState, useContext, useCallback, useMemo } from "react";
import axios from "axios";
import {
  FaSearch,
  FaEye,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
  FaBell,
  FaExclamationTriangle,
  FaCog,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/dashboardReport";

/* ---------- helpers ---------- */
const toStr = (v) => String(v ?? "").trim();

export default function DashBoardReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ inventory localStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const branchType = sessionBranchType || "HO";
  const branchId = Number(sessionBranchId || 0);

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // News ticker
  const [newsList, setNewsList] = useState([]);
  const [newsScrollPosition, setNewsScrollPosition] = useState(0);

  // Meta dropdowns
  const [branches, setBranches] = useState([]);
  const [stores, setStores] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    branch: "",
    store: "",
    vehicleName: "",
    vehicleCode: "",
    vehicleNumber: "",
  });

  // Autocomplete suggestions
  const [vehicleCodeSuggestions, setVehicleCodeSuggestions] = useState([]);
  const [vehicleNameSuggestions, setVehicleNameSuggestions] = useState([]);
  const [vehicleNoSuggestions, setVehicleNoSuggestions] = useState([]);
  const [showCodeSuggestions, setShowCodeSuggestions] = useState(false);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [showNoSuggestions, setShowNoSuggestions] = useState(false);

  // Data sections
  const [ownVehicleAlerts, setOwnVehicleAlerts] = useState([]);
  const [batteryAlerts, setBatteryAlerts] = useState([]);
  const [periodicAlerts, setPeriodicAlerts] = useState([]);
  const [jobCardEntries, setJobCardEntries] = useState([]);
  const [fuelLubricantCosts, setFuelLubricantCosts] = useState([]);

  // Pagination
  const [periodicPage, setPeriodicPage] = useState(1);
  const [jobCardPage, setJobCardPage] = useState(1);
  const itemsPerPage = 10;

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const tableHeadClass = `${isDark ? "bg-gray-800" : "bg-gray-100"}`;
  const tableRowClass = `${isDark ? "bg-gray-800" : "bg-white"}`;
  const tableCellClass = `px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`;

  /* ---------- News Ticker Auto Scroll ---------- */
  useEffect(() => {
    if (newsList.length === 0) return;

    const interval = setInterval(() => {
      setNewsScrollPosition((prev) => {
        const next = prev + 1;
        return next >= newsList.length ? 0 : next;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [newsList]);

  /* ---------- Load Meta ---------- */
  const loadMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/meta`, {
        params: { branchType, branchId },
        headers: apiHeaders,
      });

      const data = res.data || {};
      setBranches(data.branches || []);

      if (branchType !== "HO") {
        const defaultBranch =
          sessionBranchId ||
          data.session?.branchId ||
          (data.branches || []).find((b) => String(b.value) !== "Select")?.value ||
          "";

        if (defaultBranch) {
          setFilters((p) => ({ ...p, branch: String(defaultBranch) }));
          loadStores(defaultBranch);
        }
      }
    } catch (error) {
      toast.error("Failed to load dropdown meta");
    }
  }, [branchType, branchId, apiHeaders, sessionBranchId]);

  /* ---------- Load Stores ---------- */
  const loadStores = useCallback(
    async (branchIdValue) => {
      try {
        const res = await axios.get(`${API_BASE}/stores`, {
          params: {
            branchId: branchIdValue || filters.branch || branchId,
            branchType,
          },
          headers: apiHeaders,
        });

        setStores(res.data?.stores || []);
      } catch (error) {
        setStores([]);
      }
    },
    [filters.branch, branchId, branchType, apiHeaders]
  );

  /* ---------- Load News ---------- */
  const loadNews = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/news`, {
        headers: apiHeaders,
      });
      setNewsList(res.data?.news || []);
    } catch (error) {
      setNewsList([]);
    }
  }, [apiHeaders]);

  /* ---------- Autocomplete ---------- */
  const fetchVehicleCodeSuggestions = useCallback(
    async (prefix) => {
      if (!prefix || prefix.length < 1) {
        setVehicleCodeSuggestions([]);
        return;
      }

      try {
        const branchIdValue = branchType === "HO" ? filters.branch || "%" : branchId;
        const storeValue = filters.store || "%";

        const res = await axios.get(`${API_BASE}/autocomplete/vehicleCode`, {
          params: {
            prefix,
            branchId: branchIdValue,
            branchType,
            userId,
            store: storeValue,
          },
          headers: apiHeaders,
        });

        setVehicleCodeSuggestions(res.data?.items || []);
      } catch (error) {
        setVehicleCodeSuggestions([]);
      }
    },
    [branchType, branchId, userId, filters.branch, filters.store, apiHeaders]
  );

  const fetchVehicleNameSuggestions = useCallback(
    async (prefix) => {
      if (!prefix || prefix.length < 1) {
        setVehicleNameSuggestions([]);
        return;
      }

      try {
        const branchIdValue = branchType === "HO" ? filters.branch || "%" : branchId;
        const storeValue = filters.store || "%";

        const res = await axios.get(`${API_BASE}/autocomplete/vehicleName`, {
          params: {
            prefix,
            branchId: branchIdValue,
            branchType,
            userId,
            store: storeValue,
          },
          headers: apiHeaders,
        });

        setVehicleNameSuggestions(res.data?.items || []);
      } catch (error) {
        setVehicleNameSuggestions([]);
      }
    },
    [branchType, branchId, userId, filters.branch, filters.store, apiHeaders]
  );

  const fetchVehicleNoSuggestions = useCallback(
    async (prefix) => {
      if (!prefix || prefix.length < 1) {
        setVehicleNoSuggestions([]);
        return;
      }

      try {
        const branchIdValue = branchType === "HO" ? filters.branch || "%" : branchId;
        const storeValue = filters.store || "%";

        const res = await axios.get(`${API_BASE}/autocomplete/vehicleNo`, {
          params: {
            prefix,
            branchId: branchIdValue,
            branchType,
            userId,
            store: storeValue,
          },
          headers: apiHeaders,
        });

        setVehicleNoSuggestions(res.data?.items || []);
      } catch (error) {
        setVehicleNoSuggestions([]);
      }
    },
    [branchType, branchId, userId, filters.branch, filters.store, apiHeaders]
  );

  /* ---------- Load Dashboard Data ---------- */
  const loadDashboardData = useCallback(
    async (override = null) => {
      const f = override || filters;
      setLoading(true);

      try {
        const branchIdValue = branchType === "HO" ? f.branch || "%" : branchId;

        const res = await axios.get(`${API_BASE}/data`, {
          params: {
            branchType,
            branchId,
            userId,
            branch: branchIdValue,
            store: f.store || "%",
            vehicleName: f.vehicleName || "",
            vehicleCode: f.vehicleCode || "",
            vehicleNumber: f.vehicleNumber || "",
          },
          headers: apiHeaders,
        });

        const data = res.data || {};
        setOwnVehicleAlerts(data.ownVehicleAlerts || []);
        setBatteryAlerts(data.batteryAlerts || []);
        setPeriodicAlerts(data.periodicAlerts || []);
        setJobCardEntries(data.jobCardEntries || []);
        setFuelLubricantCosts(data.fuelLubricantCosts || []);

        setPeriodicPage(1);
        setJobCardPage(1);

        toast.success("Dashboard data loaded successfully");
      } catch (e) {
        toast.error(e?.response?.data?.message || "Failed to load dashboard data");
        setOwnVehicleAlerts([]);
        setBatteryAlerts([]);
        setPeriodicAlerts([]);
        setJobCardEntries([]);
        setFuelLubricantCosts([]);
      } finally {
        setLoading(false);
      }
    },
    [filters, branchType, branchId, userId, apiHeaders]
  );

  useEffect(() => {
    loadMeta();
    loadNews();
    loadDashboardData({
      branch: branchType === "HO" ? "" : sessionBranchId,
      store: "",
      vehicleName: "",
      vehicleCode: "",
      vehicleNumber: "",
    });
  }, [loadMeta, loadNews, loadDashboardData, branchType, sessionBranchId]);

  /* ---------- Handlers ---------- */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFilters((p) => {
      const updated = { ...p, [name]: value };
      if (name === "branch") updated.store = "";
      return updated;
    });

    if (name === "branch") {
      loadStores(value);
    }

    if (name === "vehicleCode") {
      fetchVehicleCodeSuggestions(value);
      setShowCodeSuggestions(true);
    }

    if (name === "vehicleName") {
      fetchVehicleNameSuggestions(value);
      setShowNameSuggestions(true);
    }

    if (name === "vehicleNumber") {
      fetchVehicleNoSuggestions(value);
      setShowNoSuggestions(true);
    }
  };

  const handleSuggestionClick = (field, value) => {
    setFilters((p) => ({ ...p, [field]: value }));

    if (field === "vehicleCode") setShowCodeSuggestions(false);
    if (field === "vehicleName") setShowNameSuggestions(false);
    if (field === "vehicleNumber") setShowNoSuggestions(false);
  };

  const handleSearch = () => {
    loadDashboardData();
  };

  const handleViewAll = () => {
    const reset = {
      branch: branchType === "HO" ? "" : sessionBranchId,
      store: "",
      vehicleName: "",
      vehicleCode: "",
      vehicleNumber: "",
    };
    setFilters(reset);
    loadDashboardData(reset);
    toast.info("Showing all records");
  };

  const handleClearFilters = () => {
    setFilters({
      branch: branchType === "HO" ? "" : sessionBranchId,
      store: "",
      vehicleName: "",
      vehicleCode: "",
      vehicleNumber: "",
    });
    setStores([]);
    setVehicleCodeSuggestions([]);
    setVehicleNameSuggestions([]);
    setVehicleNoSuggestions([]);
    toast.info("Filters cleared!");
  };

  const handlePrint = async () => {
    try {
      const branchIdValue = branchType === "HO" ? filters.branch || branchId : branchId;

      const printData = {
        branchType,
        branchidalt: branchIdValue,
        branchid: branchIdValue,
        store: filters.store || "%",
        veh_code: filters.vehicleCode || "%",
        veh_name: filters.vehicleName || "%",
        veh_no: filters.vehicleNumber || "%",
        userid: userId,
      };

      navigate("/vehicle/DashboardReport_Print", { state: printData });
    } catch (error) {
      toast.error("Failed to prepare print");
    }
  };

  const handleViewOwnVehicle = (vehicleId) => {
    navigate(`/vehicle/vehicle-details/${vehicleId}`);
  };

  const handleNewsClick = (news) => {
    toast.info(`News: ${news.heading}`);
  };

  /* ---------- Pagination ---------- */
  const periodicTotalPages = Math.ceil(periodicAlerts.length / itemsPerPage);
  const periodicIndexOfLast = periodicPage * itemsPerPage;
  const periodicIndexOfFirst = periodicIndexOfLast - itemsPerPage;
  const periodicCurrentItems = periodicAlerts.slice(
    periodicIndexOfFirst,
    periodicIndexOfLast
  );

  const jobCardTotalPages = Math.ceil(jobCardEntries.length / itemsPerPage);
  const jobCardIndexOfLast = jobCardPage * itemsPerPage;
  const jobCardIndexOfFirst = jobCardIndexOfLast - itemsPerPage;
  const jobCardCurrentItems = jobCardEntries.slice(
    jobCardIndexOfFirst,
    jobCardIndexOfLast
  );

  /* ---------- Pagination Renderer ---------- */
  const renderPagination = (currentPage, totalPages, setPage) => {
    if (totalPages <= 1) return null;

    return (
      <div className="mt-4 flex flex-col sm:flex-row justify-between items-center">
        <nav className="flex gap-1">
          <button
            onClick={() => setPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className={`px-3 py-1 border rounded text-sm ${
              currentPage === 1
                ? isDark
                  ? "border-gray-700 text-gray-500 cursor-not-allowed"
                  : "border-gray-300 text-gray-400 cursor-not-allowed"
                : isDark
                ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                : "border-gray-300 text-gray-700 hover:bg-gray-100"
            }`}
          >
            Previous
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) pageNum = i + 1;
            else if (currentPage <= 3) pageNum = i + 1;
            else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
            else pageNum = currentPage - 2 + i;

            return (
              <button
                key={pageNum}
                onClick={() => setPage(pageNum)}
                className={`px-3 py-1 border rounded text-sm ${
                  currentPage === pageNum
                    ? `${buttonClass} text-white`
                    : isDark
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                }`}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 border rounded text-sm ${
              currentPage === totalPages
                ? isDark
                  ? "border-gray-700 text-gray-500 cursor-not-allowed"
                  : "border-gray-300 text-gray-400 cursor-not-allowed"
                : isDark
                ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                : "border-gray-300 text-gray-700 hover:bg-gray-100"
            }`}
          >
            Next
          </button>
        </nav>
      </div>
    );
  };

  /* ---------- Autocomplete Dropdown ---------- */
  const AutocompleteDropdown = ({ suggestions, show, onSelect }) => {
    if (!show || suggestions.length === 0) return null;

    return (
      <div
        className={`absolute z-10 w-full mt-1 border rounded-lg shadow-lg max-h-60 overflow-auto ${
          isDark ? "bg-gray-800 border-gray-600" : "bg-white border-gray-300"
        }`}
      >
        {suggestions.map((suggestion, idx) => (
          <div
            key={idx}
            onClick={() => onSelect(suggestion)}
            className={`px-3 py-2 cursor-pointer ${
              isDark
                ? "hover:bg-gray-700 text-gray-200"
                : "hover:bg-gray-100 text-gray-900"
            }`}
          >
            {suggestion}
          </div>
        ))}
      </div>
    );
  };

  return (
    <VehicleLayoutWrapper>
      <ToastContainer />
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">DASHBOARD ALERTS</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-4">
                <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Renewal Alerts Dashboard
                </h1>
                {loading && (
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium animate-pulse ${
                      isDark ? "bg-gray-700 text-gray-200" : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    <FaCog className="inline animate-spin mr-2" />
                    Loading...
                  </span>
                )}
              </div>

              {/* News */}
              {newsList.length > 0 && (
                <div
                  className={`border rounded-lg p-4 ${
                    isDark ? "bg-gray-800 border-gray-700" : "bg-blue-50 border-blue-200"
                  }`}
                  style={{ maxHeight: "120px", overflow: "hidden" }}
                >
                  <div className="relative overflow-hidden h-24">
                    <div
                      className="transition-transform duration-500 ease-in-out"
                      style={{
                        transform: `translateY(-${newsScrollPosition * 80}px)`,
                      }}
                    >
                      {newsList.map((news, idx) => (
                        <div key={news.id || idx} className="mb-3 h-20">
                          <div className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                            ID: {news.id}
                          </div>
                          <button
                            className={`font-bold text-sm ${
                              isDark ? "text-blue-400" : "text-red-800"
                            } hover:underline`}
                            onClick={() => handleNewsClick(news)}
                          >
                            {news.heading}
                          </button>
                          <div
                            className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            {news.shortDescription}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 ml-6">
              <div className={`text-right text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                INFRA SCREEN : B-43/1
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" +
                      (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                      (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handlePrint}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaPrint className="w-4 h-4" />
                Print
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`mb-8 p-6 rounded-xl border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={branchType !== "HO"}
                  >
                    <option value="">Select Branch</option>
                    {branches
                      .filter((b) => String(b.value) !== "Select")
                      .map((b) => (
                        <option key={b.id || b.value} value={b.value}>
                          {b.name || b.label}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    name="store"
                    value={filters.store}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="">Select Store</option>
                    {stores
                      .filter((s) => String(s.value) !== "Select")
                      .map((s) => (
                        <option key={s.id || s.value} value={s.value}>
                          {s.name || s.label}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="relative">
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={handleChange}
                    onFocus={() => {
                      if (filters.vehicleName) {
                        fetchVehicleNameSuggestions(filters.vehicleName);
                        setShowNameSuggestions(true);
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowNameSuggestions(false), 200)}
                    className={inputClass}
                    placeholder="Enter vehicle name"
                    autoComplete="off"
                  />
                  <AutocompleteDropdown
                    suggestions={vehicleNameSuggestions}
                    show={showNameSuggestions}
                    onSelect={(value) => handleSuggestionClick("vehicleName", value)}
                  />
                </div>

                <div className="relative">
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={handleChange}
                    onFocus={() => {
                      if (filters.vehicleCode) {
                        fetchVehicleCodeSuggestions(filters.vehicleCode);
                        setShowCodeSuggestions(true);
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowCodeSuggestions(false), 200)}
                    className={inputClass}
                    placeholder="Enter vehicle code"
                    autoComplete="off"
                  />
                  <AutocompleteDropdown
                    suggestions={vehicleCodeSuggestions}
                    show={showCodeSuggestions}
                    onSelect={(value) => handleSuggestionClick("vehicleCode", value)}
                  />
                </div>

                <div className="relative">
                  <label className={labelClass}>Vehicle Number</label>
                  <input
                    type="text"
                    name="vehicleNumber"
                    value={filters.vehicleNumber}
                    onChange={handleChange}
                    onFocus={() => {
                      if (filters.vehicleNumber) {
                        fetchVehicleNoSuggestions(filters.vehicleNumber);
                        setShowNoSuggestions(true);
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowNoSuggestions(false), 200)}
                    className={inputClass}
                    placeholder="Enter vehicle number"
                    autoComplete="off"
                  />
                  <AutocompleteDropdown
                    suggestions={vehicleNoSuggestions}
                    show={showNoSuggestions}
                    onSelect={(value) => handleSuggestionClick("vehicleNumber", value)}
                  />
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleClearFilters}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                >
                  Clear All Filters
                </button>

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleViewAll}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    <FaEye />
                    View All
                  </button>

                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                  >
                    <FaSearch />
                    Search Records
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Own Vehicle Renewal Alerts */}
          <div className="mb-8">
            <div className={`rounded-t-xl p-4 ${buttonClass} text-white`}>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FaBell />
                Own Vehicle Renewal Alerts
                <span className="ml-2 px-3 py-1 bg-white/20 rounded-full text-sm">
                  {ownVehicleAlerts.length}
                </span>
              </h2>
            </div>

            <div
              className={`rounded-b-xl shadow-sm border overflow-hidden ${
                isDark ? "border-gray-700" : "border-gray-200"
              }`}
            >
              {ownVehicleAlerts.length === 0 ? (
                <div className="text-center py-12">
                  <FaInfoCircle
                    className={`w-12 h-12 mx-auto mb-4 ${
                      isDark ? "text-gray-600" : "text-gray-400"
                    }`}
                  />
                  <p className={isDark ? "text-gray-400" : "text-gray-500"}>
                    No renewal alerts found
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={tableHeadClass}>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold">S.No</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold">Store</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold">Vehicle Main Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold">Vehicle Code</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold">Vehicle Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold">Vehicle Number</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold">Vehicle Type</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold">Insurance</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold">FC</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold">Road Tax</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold">Permit</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold">Local Permit</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold">TR Permit</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold">Details</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {ownVehicleAlerts.map((item, idx) => (
                        <tr
                          key={item.fld_vehicleid || idx}
                          className={`${tableRowClass} transition-colors`}
                        >
                          <td className={tableCellClass}>{idx + 1}</td>
                          <td className={tableCellClass}>{item.fld_storename || "-"}</td>
                          <td className={tableCellClass}>{item.VehicleMainType || "-"}</td>
                          <td className={tableCellClass}>{item.fld_vehicle_code || "-"}</td>
                          <td className={tableCellClass}>{item.fld_vehicle_name || "-"}</td>
                          <td className={tableCellClass}>{item.fld_vehicle_regno || "-"}</td>
                          <td className={tableCellClass}>{item.fld_vehicletype || "-"}</td>
                          <td className="px-4 py-3 text-sm text-center font-semibold text-red-600">
                            {item.fld_insuranceexpire_date || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-center font-semibold text-red-600">
                            {item.fld_fcrenewaldate || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-center font-semibold text-red-600">
                            {item.fld_roadtaxto_date || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-center font-semibold text-red-600">
                            {item.fld_pemitexpire_date || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-center font-semibold text-red-600">
                            {item.fld_localpemitexpire_date || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-center font-semibold text-red-600">
                            {item.fld_TRpemitexpire_date || "-"}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleViewOwnVehicle(item.fld_vehicleid)}
                              className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Battery Card Renewal Alerts */}
          <div className="mb-8">
            <div className={`rounded-t-xl p-4 ${buttonClass} text-white`}>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FaExclamationTriangle />
                Battery Card Renewal Alerts
                <span className="ml-2 px-3 py-1 bg-white/20 rounded-full text-sm">
                  {batteryAlerts.length}
                </span>
              </h2>
            </div>

            <div
              className={`rounded-b-xl shadow-sm border overflow-hidden ${
                isDark ? "border-gray-700" : "border-gray-200"
              }`}
            >
              {batteryAlerts.length === 0 ? (
                <div className="text-center py-12">
                  <FaInfoCircle
                    className={`w-12 h-12 mx-auto mb-4 ${
                      isDark ? "text-gray-600" : "text-gray-400"
                    }`}
                  />
                  <p className={isDark ? "text-gray-400" : "text-gray-500"}>
                    No battery alerts found
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={tableHeadClass}>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold">S.No</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold">Store</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold">Vehicle Main Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold">Vehicle Code</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold">Vehicle Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold">Vehicle Number</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold">Battery Renewal Alert</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {batteryAlerts.map((item, idx) => (
                        <tr key={item.fld_vehicleid || idx} className={tableRowClass}>
                          <td className={tableCellClass}>{idx + 1}</td>
                          <td className={tableCellClass}>{item.fld_storename || "-"}</td>
                          <td className={tableCellClass}>{item.VehicleMainType || "-"}</td>
                          <td className={tableCellClass}>{item.fld_vehicle_code || "-"}</td>
                          <td className={tableCellClass}>{item.fld_vehicle_name || "-"}</td>
                          <td className={tableCellClass}>{item.fld_vehicle_regno || "-"}</td>
                          <td className="px-4 py-3 text-sm text-center font-semibold text-red-600 animate-pulse">
                            {item.fld_alert || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Periodic Entry Alerts */}
          <div className="mb-8">
            <div className={`rounded-t-xl p-4 ${buttonClass} text-white`}>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FaBell />
                Periodic Entry Alerts
                <span className="ml-2 px-3 py-1 bg-white/20 rounded-full text-sm">
                  {periodicAlerts.length}
                </span>
              </h2>
            </div>

            <div
              className={`rounded-b-xl shadow-sm border overflow-hidden ${
                isDark ? "border-gray-700" : "border-gray-200"
              }`}
            >
              {periodicAlerts.length === 0 ? (
                <div className="text-center py-12">
                  <FaInfoCircle
                    className={`w-12 h-12 mx-auto mb-4 ${
                      isDark ? "text-gray-600" : "text-gray-400"
                    }`}
                  />
                  <p className={isDark ? "text-gray-400" : "text-gray-500"}>
                    No periodic alerts found
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className={tableHeadClass}>
                        <tr>
                          <th className="px-2 py-2 text-left text-xs font-semibold">S.No</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold">Branch</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold">Vehicle Main Type</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold">Vehicle Code</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold">Vehicle Name</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold">Vehicle Reg No</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold">SubAssembly</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold">Description</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold">Oil Grade/Part No</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold">Last Changed Date</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold">Last Changed Hr/KM</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold">Service Limit</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold">Next Service Hr/KM</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {periodicCurrentItems.map((item, idx) => (
                          <tr key={idx} className={tableRowClass}>
                            <td className="px-2 py-2">{periodicIndexOfFirst + idx + 1}</td>
                            <td className="px-2 py-2">{item.fld_branchname || item.fld_Branchname || "-"}</td>
                            <td className="px-2 py-2">{item.VehicleMainType || "-"}</td>
                            <td className="px-2 py-2">{item.fld_vehicle_code || "-"}</td>
                            <td className="px-2 py-2">{item.fld_vehicle_name || "-"}</td>
                            <td className="px-2 py-2">{item.fld_vehicle_regno || "-"}</td>
                            <td className="px-2 py-2">{item.SubAssembly || item.fld_subassembly || "-"}</td>
                            <td className="px-2 py-2">{item.Description || item.fld_description || "-"}</td>
                            <td className="px-2 py-2">{item.OilGrade || item.PartNo || "-"}</td>
                            <td className="px-2 py-2">{item.LastChangedDate || item.fld_lastchangeddate || "-"}</td>
                            <td className="px-2 py-2">{item.LastChangedHrKM || item.fld_lastchangedhrkm || "-"}</td>
                            <td className="px-2 py-2">{item.ServiceLimit || item.fld_servicelimit || "-"}</td>
                            <td className="px-2 py-2 font-semibold text-red-600">
                              {item.NextServiceHrKM || item.fld_nextservicehrkm || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="p-4">
                    {renderPagination(periodicPage, periodicTotalPages, setPeriodicPage)}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Job Card Entries */}
          <div className="mb-8">
            <div className={`rounded-t-xl p-4 ${buttonClass} text-white`}>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FaCog />
                Job Card Entries
                <span className="ml-2 px-3 py-1 bg-white/20 rounded-full text-sm">
                  {jobCardEntries.length}
                </span>
              </h2>
            </div>

            <div
              className={`rounded-b-xl shadow-sm border overflow-hidden ${
                isDark ? "border-gray-700" : "border-gray-200"
              }`}
            >
              {jobCardEntries.length === 0 ? (
                <div className="text-center py-12">
                  <FaInfoCircle
                    className={`w-12 h-12 mx-auto mb-4 ${
                      isDark ? "text-gray-600" : "text-gray-400"
                    }`}
                  />
                  <p className={isDark ? "text-gray-400" : "text-gray-500"}>
                    No job card entries found
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className={tableHeadClass}>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold">S.No</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold">Branch</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold">Job Card No</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold">Vehicle Code</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold">Vehicle Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold">Vehicle Number</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold">Status</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {jobCardCurrentItems.map((item, idx) => (
                          <tr key={idx} className={tableRowClass}>
                            <td className={tableCellClass}>{jobCardIndexOfFirst + idx + 1}</td>
                            <td className={tableCellClass}>{item.fld_branchname || item.fld_Branchname || "-"}</td>
                            <td className={tableCellClass}>{item.fld_jobcardno || item.JobCardNo || "-"}</td>
                            <td className={tableCellClass}>{item.fld_vehicle_code || "-"}</td>
                            <td className={tableCellClass}>{item.fld_vehicle_name || "-"}</td>
                            <td className={tableCellClass}>{item.fld_vehicle_regno || "-"}</td>
                            <td className={tableCellClass}>{item.fld_date || item.JobCardDate || "-"}</td>
                            <td className={tableCellClass}>{item.fld_status || item.Status || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="p-4">
                    {renderPagination(jobCardPage, jobCardTotalPages, setJobCardPage)}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Fuel & Lubricant Costs */}
          <div className="mb-2">
            <div className={`rounded-t-xl p-4 ${buttonClass} text-white`}>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FaBell />
                Fuel & Lubricant Costs
                <span className="ml-2 px-3 py-1 bg-white/20 rounded-full text-sm">
                  {fuelLubricantCosts.length}
                </span>
              </h2>
            </div>

            <div
              className={`rounded-b-xl shadow-sm border overflow-hidden ${
                isDark ? "border-gray-700" : "border-gray-200"
              }`}
            >
              {fuelLubricantCosts.length === 0 ? (
                <div className="text-center py-12">
                  <FaInfoCircle
                    className={`w-12 h-12 mx-auto mb-4 ${
                      isDark ? "text-gray-600" : "text-gray-400"
                    }`}
                  />
                  <p className={isDark ? "text-gray-400" : "text-gray-500"}>
                    No fuel/lubricant cost data found
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className={tableHeadClass}>
                      <tr>
                        {Object.keys(fuelLubricantCosts[0] || {}).map((key) => (
                          <th
                            key={key}
                            className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap"
                          >
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {fuelLubricantCosts.map((row, idx) => (
                        <tr key={idx} className={tableRowClass}>
                          {Object.keys(fuelLubricantCosts[0] || {}).map((key) => (
                            <td key={key} className={tableCellClass}>
                              {toStr(row[key]) || "-"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}